import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { documentExportUrl } from '@/entities/document';
import { useCopyLink } from '@/features/resource-nav';
import { Icons, MenuItem, MenuList, MenuRow, Popover } from '@/shared/ui';

import styles from './DocumentPage.module.css';

export interface DocumentMenuActions {
  onInsertMarkdown: () => void;
  onVersions: () => void;
  onDuplicate: () => void;
  onAccess: () => void;
  onDelete: () => void;
  /** Offered to those who may edit the note's details, in a team workspace (labels live there). */
  onLabels?: (() => void) | undefined;
}

/**
 * The note's actions in three groups - Export (one level down, so three formats are one row),
 * Document, Management (the dangerous one apart, in red) - instead of one long flat list.
 */
export function DocumentMenu({
  documentId,
  mayEdit,
  canManage,
  canDelete,
  actions,
}: {
  documentId: string;
  mayEdit: boolean;
  canManage: boolean;
  canDelete: boolean;
  actions: DocumentMenuActions;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<'root' | 'export'>('root');
  const copyLink = useCopyLink();
  return (
    <Popover
      label={t('documents.pageMenu')}
      width={280}
      alignEnd
      onOpenChange={(open) => {
        if (!open) setView('root');
      }}
      trigger={(props) => (
        <button
          type="button"
          {...props}
          className={styles.more}
          aria-label={t('documents.pageMenu')}
          data-testid="document-page-menu"
        >
          <Icons.more />
        </button>
      )}
    >
      {(close) => {
        const run = (action: () => void) => () => {
          close();
          action();
        };
        if (view === 'export') {
          return (
            <MenuList>
              <MenuRow>
                <MenuItem
                  icon={<Icons.chevronLeft />}
                  onClick={() => {
                    setView('root');
                  }}
                >
                  {t('documents.pageActions.export')}
                </MenuItem>
              </MenuRow>
              {(['md', 'docx', 'pdf'] as const).map((format, index) => (
                <MenuRow key={format} separated={index === 0}>
                  <a
                    className={styles.menuLink}
                    href={documentExportUrl(documentId, format)}
                    download
                    data-testid={`export-${format}`}
                    onClick={close}
                  >
                    <Icons.export /> {t(`documents.export.${format}`)}
                  </a>
                </MenuRow>
              ))}
            </MenuList>
          );
        }
        return (
          <MenuList>
            <MenuRow>
              <MenuItem
                icon={<Icons.export />}
                trailing={<Icons.chevronRight />}
                data-testid="menu-export"
                onClick={() => {
                  setView('export');
                }}
              >
                {t('documents.pageActions.export')}
              </MenuItem>
            </MenuRow>
            <MenuRow separated>
              <p className={styles.menuGroup}>{t('documents.pageActions.document')}</p>
            </MenuRow>
            {mayEdit && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.import />}
                  data-testid="menu-insert-markdown"
                  onClick={run(actions.onInsertMarkdown)}
                >
                  {t('documents.pageActions.insertMarkdown')}
                </MenuItem>
              </MenuRow>
            )}
            <MenuRow>
              <MenuItem
                icon={<Icons.link />}
                data-testid="menu-copy-link"
                onClick={run(() => {
                  copyLink(`/app/documents/${documentId}`);
                })}
              >
                {t('links.copy')}
              </MenuItem>
            </MenuRow>
            {actions.onLabels && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.tag />}
                  data-testid="menu-labels"
                  onClick={run(actions.onLabels)}
                >
                  {t('resourceLabels.action')}
                </MenuItem>
              </MenuRow>
            )}
            <MenuRow>
              <MenuItem icon={<Icons.history />} onClick={run(actions.onVersions)}>
                {t('documents.versions.title')}
              </MenuItem>
            </MenuRow>
            <MenuRow>
              <MenuItem icon={<Icons.copy />} onClick={run(actions.onDuplicate)}>
                {t('documents.duplicate')}
              </MenuItem>
            </MenuRow>
            {(canManage || canDelete) && (
              <MenuRow separated>
                <p className={styles.menuGroup}>{t('documents.pageActions.manage')}</p>
              </MenuRow>
            )}
            {canManage && (
              <MenuRow>
                <MenuItem icon={<Icons.lock />} onClick={run(actions.onAccess)}>
                  {t('documents.access.title')}
                </MenuItem>
              </MenuRow>
            )}
            {canDelete && (
              <MenuRow>
                <MenuItem icon={<Icons.trash />} danger onClick={run(actions.onDelete)}>
                  {t('documents.delete')}
                </MenuItem>
              </MenuRow>
            )}
          </MenuList>
        );
      }}
    </Popover>
  );
}
